import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { getCrmIntegrations } from '~/api/crms';
import { CrmType } from '~/enums/CrmType';
import { useAsync } from '~/hooks/useAsync';
import CardList from '~/ui/components/CardList/CardList';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import Text from '~/ui/components/Text/Text';

import CrmIntegrationCard from './CrmIntegrationCard/CrmIntegrationCard';

function testHasIntegration(integration) {
  let hasIntegration;
  switch (integration.provider) {
    case CrmType.Salesforce:
      hasIntegration = true;
      break;
    default:
      hasIntegration = false;
  }
  return hasIntegration;
}

function CrmIntegrations() {
  const intl = useIntl();
  const [crmIntegrations, setCrmIntegrations] = useState([]);
  const [refetchId, setRefetchId] = useState(uuid());

  const { isLoading } = useAsync(getCrmIntegrations, null, {
    condition: refetchId,
    deps: [refetchId],
    successHandler: (integrations) => {
      const integrationsWithPermissions = integrations.map((integration) => ({
        ...integration,
        disabled: !testHasIntegration(integration),
      }));
      setCrmIntegrations(integrationsWithPermissions);
    },
  });

  async function handleRefetch() {
    setRefetchId(uuid());
  }

  if (!crmIntegrations) {
    return (
      <Text variant="m-spaced">
        {intl.formatMessage({
          id: 'CrmIntegrations.fetchError',
          defaultMessage:
            'Unable to fetch third party integrations, please try again.',
          description: 'Error message when CRM integrations fail to load',
        })}
      </Text>
    );
  }

  return (
    <CardList
      title={intl.formatMessage({
        id: 'CrmIntegrations.title',
        defaultMessage: 'CRM / Sales',
        description: 'Title for CRM integrations section',
      })}
    >
      <LoadingContainer isLoading={isLoading}>
        {crmIntegrations.map((integration) => (
          <CrmIntegrationCard
            key={integration.provider}
            disabled={!!integration && integration.disabled}
            crmIntegration={integration}
            crmType={integration.provider}
            isRefetching={isLoading}
            onRefetch={handleRefetch}
          />
        ))}
      </LoadingContainer>
    </CardList>
  );
}

export default CrmIntegrations;
